/**
 * WalkieFAB v3 — 2026-02-27
 *
 * UX rewrite per user ask: "we should push the mic and it should open a
 * screen where you would hold the mic button — the mic button and menu
 * don't work great together". The FAB is now tap-only (opens the full
 * radio sheet); hold-to-record lives on the big center button INSIDE
 * the sheet so there's no ambiguity between "I want to talk" and "I
 * want to see the feed".
 *
 * Plus: real-time SSE listener. Voice clips now come with an in-app
 * cue (chime + toast + auto-feed-refresh) so users on desktop or
 * in-app iOS see them the moment they arrive. Fixes the "we can talk
 * but nobody's being notified" report — the old build only fired web
 * push, and most crew aren't enrolled.
 *
 * Access rules unchanged: SM 24/7, everyone else via zone sheet, DMs
 * never. Channel picker (All Store / Managers / LP), per-channel mute
 * (Loud / Quiet / Off), Emergency (manager-only), Raise Hand.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Microphone, X, Play, Waveform, Hand, Warning, SpeakerHigh, SpeakerSlash, BellSlash } from "@phosphor-icons/react";
import { playWalkieChime, playWalkieEmergencyChime, playWalkieStartChirp, playWalkieEndChirp } from "../lib/sounds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RECORD_MS = 15000;

const CHANNEL_LABELS = { all_store: "All Store", managers: "Managers", lp: "LP" };
const CHANNEL_COLOR = {
    all_store: "from-orange-500 to-orange-600",
    managers: "from-indigo-500 to-indigo-600",
    lp: "from-slate-700 to-slate-900",
};

export default function WalkieFAB({ crewId }) {
    const [state, setState] = useState({ eligible: false, channels: [], role: "crew", is_lp: false, prefs: {} });
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [activeChannel, setActiveChannel] = useState("all_store");
    const [clips, setClips] = useState([]);
    const [busy, setBusy] = useState(false);
    // Emergency v3 (2026-02-27) — no more "arm-then-hold" flow. Tap
    // Emergency → pick one of the 5 canned codes → one-tap send.
    const [emergencyPickerOpen, setEmergencyPickerOpen] = useState(false);
    const [emergencyCodes, setEmergencyCodes] = useState([]);
    const [unread, setUnread] = useState(0);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const startTsRef = useRef(0);
    const stopTimerRef = useRef(null);
    const tickTimerRef = useRef(null);

    const refreshEligibility = useCallback(async () => {
        if (!crewId) return;
        try {
            const r = await axios.get(`${API}/voice/eligible/${crewId}`);
            const data = r.data || {};
            setState({
                eligible: !!data.eligible,
                channels: data.channels || [],
                role: data.role || "crew",
                is_lp: !!data.is_lp,
                prefs: data.prefs || {},
            });
            // If the currently selected channel is no longer available, fall back to All Store
            if (data.channels && !data.channels.includes(activeChannel)) {
                setActiveChannel(data.channels[0] || "all_store");
            }
        } catch { setState((s) => ({ ...s, eligible: false })); }
    }, [crewId, activeChannel]);

    useEffect(() => {
        refreshEligibility();
        const t = setInterval(refreshEligibility, 60000);
        return () => clearInterval(t);
    }, [refreshEligibility]);

    const loadFeed = useCallback(async () => {
        if (!crewId) return;
        try {
            const r = await axios.get(`${API}/voice/feed/${crewId}`, { params: { channel: activeChannel } });
            setClips(r.data?.clips || []);
        } catch { /* keep prev */ }
    }, [crewId, activeChannel]);

    useEffect(() => { if (sheetOpen) { loadFeed(); setUnread(0); } }, [sheetOpen, activeChannel, loadFeed]);

    // Real-time SSE cue for incoming voice clips. App.js re-broadcasts
    // every SSE event on `wow:notification`; we tune into voice kinds.
    // On any voice event NOT from us, play the chime, bump the unread
    // badge, and auto-refresh the feed if the sheet is open.
    useEffect(() => {
        if (!crewId) return undefined;
        const onNotif = (e) => {
            const evt = e?.detail || {};
            const kind = evt.kind || "";
            // Real-time transcript patch — when the background Whisper
            // job returns for a clip we just showed with the "🎙️ Voice
            // message" placeholder, this event carries the actual text
            // so we can update the row in-place without another feed
            // fetch. Applies to sender AND recipients so the sender
            // sees their own words appear right after release.
            if (kind === "voice_clip_transcript" && evt.clip_id && evt.transcript) {
                setClips((prev) => prev.map((c) => c.id === evt.clip_id ? { ...c, transcript: evt.transcript } : c));
                return;
            }
            if (kind !== "voice_clip" && kind !== "voice_emergency" && kind !== "voice_raise_hand") return;
            if (evt.sender_id && evt.sender_id === crewId) return;
            try {
                if (kind === "voice_emergency") playWalkieEmergencyChime();
                else playWalkieChime();
            } catch { /* ignore audio errors */ }
            if (sheetOpen) {
                loadFeed();
            } else {
                setUnread((n) => Math.min(n + 1, 99));
                toast(evt.title || "🎙️ New walkie clip", {
                    description: evt.body || undefined,
                    action: { label: "Open", onClick: () => setSheetOpen(true) },
                    duration: kind === "voice_emergency" ? 12000 : 6000,
                });
            }
        };
        window.addEventListener("wow:notification", onNotif);
        return () => window.removeEventListener("wow:notification", onNotif);
    }, [crewId, sheetOpen, loadFeed]);

    const startRecording = useCallback(async () => {
        if (recording || busy) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mr = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm",
            });
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                const duration = (Date.now() - startTsRef.current) / 1000;
                try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
                streamRef.current = null;
                setElapsed(0);
                if (blob.size < 200) {
                    toast.error("Too short — hold longer next time.");
                    return;
                }
                setBusy(true);
                try {
                    const fd = new FormData();
                    fd.append("audio", blob, "clip.webm");
                    fd.append("crew_id", crewId);
                    fd.append("duration_s", String(duration.toFixed(2)));
                    fd.append("channel", activeChannel);
                    fd.append("priority", "normal");
                    const r = await axios.post(`${API}/voice/transmit`, fd);
                    const preview = r.data?.transcript ? `"${r.data.transcript.slice(0, 60)}${r.data.transcript.length > 60 ? "…" : ""}"` : "sent";
                    toast.success(`Sent to ${CHANNEL_LABELS[activeChannel]}: ${preview}`);
                    loadFeed();
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't send.");
                } finally {
                    setBusy(false);
                }
            };
            recorderRef.current = mr;
            mr.start();
            startTsRef.current = Date.now();
            setRecording(true);
            // Classic walkie "key up" chirp so the sender hears the
            // channel open. Fires AFTER MediaRecorder.start so the
            // chirp doesn't leak into the leading edge of the audio.
            try { playWalkieStartChirp(); } catch { /* ignore */ }
            tickTimerRef.current = setInterval(() => {
                setElapsed((Date.now() - startTsRef.current) / 1000);
            }, 100);
            stopTimerRef.current = setTimeout(() => {
                if (recorderRef.current?.state === "recording") {
                    recorderRef.current.stop();
                    setRecording(false);
                }
            }, MAX_RECORD_MS);
        } catch {
            toast.error("Mic access denied. Enable microphone in Settings.");
        }
    }, [crewId, recording, busy, activeChannel, loadFeed]);

    const stopRecording = useCallback(() => {
        if (!recording) return;
        try {
            if (recorderRef.current?.state === "recording") recorderRef.current.stop();
        } catch { /* ignore */ }
        clearTimeout(stopTimerRef.current);
        clearInterval(tickTimerRef.current);
        setRecording(false);
        // "Key down" chirp confirms the clip was captured before the
        // upload finishes; contrasts with the receive chime.
        try { playWalkieEndChirp(); } catch { /* ignore */ }
    }, [recording]);

    const onRaiseHand = useCallback(async () => {
        if (!crewId || busy) return;
        setBusy(true);
        try {
            await axios.post(`${API}/voice/raise-hand`, { crew_id: crewId });
            toast.success("🙋 Sent to All Store — help is on the way.");
            loadFeed();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send.");
        } finally { setBusy(false); }
    }, [crewId, busy, loadFeed]);

    const setChannelPref = useCallback(async (channel, mode) => {
        try {
            await axios.post(`${API}/voice/prefs`, { crew_id: crewId, channel, mode });
            setState((s) => ({ ...s, prefs: { ...s.prefs, [channel]: mode } }));
        } catch { toast.error("Couldn't save preference."); }
    }, [crewId]);

    if (!state.eligible) return null;

    const isManager = state.role === "manager";
    const fabColor = `bg-gradient-to-br ${CHANNEL_COLOR[activeChannel]} hover:scale-105`;

    return (
        <>
            {/* FAB — tap only. Opens the radio sheet. Hold-to-talk lives
                on the big center mic button inside the sheet. (v3
                rewrite 2026-02-27.) */}
            <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className={`fixed right-4 bottom-5 z-50 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-[0_10px_24px_rgba(0,0,0,0.35),_0_0_0_3px_rgba(255,255,255,0.9)] transition-transform ${fabColor}`}
                data-testid="walkie-fab"
                aria-label="Open walkie"
            >
                <Microphone weight="fill" className="w-6 h-6" />
                {unread > 0 && (
                    <span
                        className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center shadow-md ring-2 ring-white"
                        data-testid="walkie-fab-unread"
                        aria-label={`${unread} unread clip${unread === 1 ? "" : "s"}`}
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {/* Channel chip above FAB — shows which channel you'll send to */}
            {!sheetOpen && (
                <div className="fixed right-20 bottom-8 z-50 pointer-events-none">
                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-white text-slate-900 shadow-md`}>
                        {CHANNEL_LABELS[activeChannel]}
                    </span>
                </div>
            )}

            {/* Sheet */}
            {sheetOpen && (
                <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setSheetOpen(false)} data-testid="walkie-sheet">
                    <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <Waveform weight="fill" className="w-5 h-5 text-orange-500" />
                                <h3 className="font-black text-base" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>WOW Walkie</h3>
                            </div>
                            <button onClick={() => setSheetOpen(false)} data-testid="walkie-sheet-close" className="p-1 rounded-md hover:bg-slate-100" aria-label="Close">
                                <X weight="bold" className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Channel picker */}
                        <div className="px-4 pt-3 pb-2 flex gap-2 overflow-x-auto" data-testid="walkie-channel-picker">
                            {state.channels.map((ch) => {
                                const active = ch === activeChannel;
                                const mode = state.prefs[ch] || "loud";
                                return (
                                    <div key={ch} className="flex-shrink-0 flex items-center gap-1">
                                        <button
                                            onClick={() => setActiveChannel(ch)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all ${active ? `bg-gradient-to-r ${CHANNEL_COLOR[ch]} text-white shadow-md` : "bg-slate-100 text-slate-600"}`}
                                            data-testid={`walkie-channel-${ch}`}
                                        >
                                            {CHANNEL_LABELS[ch]}
                                        </button>
                                        <button
                                            onClick={() => {
                                                const next = mode === "loud" ? "quiet" : mode === "quiet" ? "off" : "loud";
                                                setChannelPref(ch, next);
                                            }}
                                            className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                                            title={`Current: ${mode} — tap to cycle`}
                                            data-testid={`walkie-mute-${ch}`}
                                        >
                                            {mode === "loud" && <SpeakerHigh weight="bold" className="w-3.5 h-3.5 text-emerald-600" />}
                                            {mode === "quiet" && <BellSlash weight="bold" className="w-3.5 h-3.5 text-amber-600" />}
                                            {mode === "off" && <SpeakerSlash weight="bold" className="w-3.5 h-3.5 text-rose-600" />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="px-4 pb-2 flex gap-2">
                            <button
                                onClick={onRaiseHand}
                                disabled={busy}
                                className="flex-1 h-10 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-black text-xs uppercase tracking-wider disabled:opacity-60 flex items-center justify-center gap-1"
                                data-testid="walkie-raise-hand"
                            >
                                <Hand weight="fill" className="w-4 h-4" /> Raise Hand
                            </button>
                            {isManager && (
                                <button
                                    onClick={async () => {
                                        // Lazy-load codes on first open so
                                        // we don't burn a request unless
                                        // the user actually taps.
                                        if (emergencyCodes.length === 0) {
                                            try {
                                                const r = await axios.get(`${API}/voice/emergency-codes`);
                                                setEmergencyCodes(r.data?.codes || []);
                                            } catch { /* keep empty; picker will show a hint */ }
                                        }
                                        setEmergencyPickerOpen(true);
                                    }}
                                    className="h-10 px-3 rounded-full font-black text-xs uppercase tracking-wider flex items-center gap-1 bg-red-100 text-red-700 border border-red-300 hover:bg-red-200"
                                    data-testid="walkie-emergency-open"
                                >
                                    <Warning weight="fill" className="w-4 h-4" />
                                    Emergency
                                </button>
                            )}
                        </div>

                        {/* Big center push-to-talk mic. Hold to record.
                            (v3 UX 2026-02-27 — moved off the FAB so
                            "open sheet" and "record" are separate
                            gestures per user ask.) */}
                        <div className="px-4 pt-1 pb-3 flex flex-col items-center gap-2 border-b border-slate-100" data-testid="walkie-mic-panel">
                            <button
                                type="button"
                                onPointerDown={(e) => { e.preventDefault(); startRecording(); }}
                                onPointerUp={stopRecording}
                                onPointerCancel={stopRecording}
                                onPointerLeave={() => recording && stopRecording()}
                                onContextMenu={(e) => e.preventDefault()}
                                disabled={busy}
                                style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
                                className={`relative w-24 h-24 rounded-full flex items-center justify-center text-white shadow-[0_12px_32px_rgba(0,0,0,0.25)] transition-transform ${
                                    recording
                                        ? "scale-110 bg-rose-500 animate-pulse"
                                        : `bg-gradient-to-br ${CHANNEL_COLOR[activeChannel]}`
                                } ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
                                data-testid="walkie-mic-hold"
                                aria-label="Hold to talk"
                            >
                                <Microphone weight="fill" className="w-9 h-9" />
                                {recording && (
                                    <span className="absolute -bottom-1 -right-1 min-w-[38px] h-6 px-2 rounded-full bg-white text-rose-600 text-[11px] font-black flex items-center justify-center shadow-md ring-2 ring-rose-500">
                                        {elapsed.toFixed(1)}s
                                    </span>
                                )}
                            </button>
                            <p className="text-[11px] text-slate-500 font-black uppercase tracking-wider">
                                {recording
                                    ? "Release to send"
                                    : busy
                                        ? "Sending…"
                                        : `Hold to send to ${CHANNEL_LABELS[activeChannel]}`}
                            </p>
                        </div>


                        {/* Emergency canned-message picker. One tap →
                            broadcasts across every channel bypassing
                            mutes. No recording, no arming — per user
                            ask: "emergency is for 1 key and run with it".
                            (2026-02-27.) */}
                        {emergencyPickerOpen && (
                            <div
                                className="fixed inset-0 z-[60] bg-slate-900/70 flex items-end sm:items-center justify-center p-4"
                                onClick={() => setEmergencyPickerOpen(false)}
                                data-testid="walkie-emergency-picker"
                            >
                                <div
                                    className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="px-4 py-3 bg-red-600 text-white flex items-center gap-2">
                                        <Warning weight="fill" className="w-5 h-5" />
                                        <p className="text-sm font-black uppercase tracking-wider flex-1">Emergency broadcast</p>
                                        <button
                                            onClick={() => setEmergencyPickerOpen(false)}
                                            className="text-white/80 hover:text-white text-lg leading-none px-1"
                                            aria-label="Close"
                                            data-testid="walkie-emergency-close"
                                        >×</button>
                                    </div>
                                    <p className="px-4 pt-3 text-[11px] text-slate-500 leading-snug">
                                        One tap sends to <strong>every crew member</strong> on the sheet, bypassing all mutes and channels. Pick the message that fits — no talking required.
                                    </p>
                                    <ul className="py-2">
                                        {emergencyCodes.length === 0 && (
                                            <li className="px-4 py-3 text-sm text-slate-400 italic">Loading codes…</li>
                                        )}
                                        {emergencyCodes.map((c) => (
                                            <li key={c.key}>
                                                <button
                                                    disabled={busy}
                                                    onClick={async () => {
                                                        if (busy) return;
                                                        setBusy(true);
                                                        // Fire the chime immediately so the
                                                        // sender hears the emergency tone as
                                                        // instant confirmation their broadcast
                                                        // went out — the SSE listener still
                                                        // skips self to avoid double-playing.
                                                        try { playWalkieEmergencyChime(); } catch { /* ignore */ }
                                                        try {
                                                            const r = await axios.post(`${API}/voice/emergency`, {
                                                                crew_id: crewId,
                                                                code: c.key,
                                                            });
                                                            toast.success(`🚨 ${c.label} sent to ${r.data?.listeners || 0} crew`);
                                                            setEmergencyPickerOpen(false);
                                                            loadFeed();
                                                        } catch (e) {
                                                            toast.error(e?.response?.data?.detail || "Couldn't broadcast.");
                                                        } finally {
                                                            setBusy(false);
                                                        }
                                                    }}
                                                    className="w-full text-left px-4 py-3 hover:bg-red-50 focus:bg-red-50 focus:outline-none disabled:opacity-50"
                                                    data-testid={`walkie-emergency-code-${c.key}`}
                                                >
                                                    <p className="text-sm font-black text-red-700">{c.label}</p>
                                                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{c.body.replace(/^🚨\s*/, "")}</p>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-500 uppercase tracking-wider font-black">
                                        No undo — pick carefully.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Feed */}
                        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
                            {clips.length === 0 ? (
                                <div className="text-center py-10 text-slate-500 text-sm">
                                    No voice messages in <strong>{CHANNEL_LABELS[activeChannel]}</strong> yet.<br />
                                    <span className="text-xs">Hold the mic above to send the first one.</span>
                                </div>
                            ) : clips.map((c) => (
                                <ClipCard key={c.id} clip={c} crewId={crewId} isManager={isManager} onDeleted={loadFeed} />
                            ))}
                        </div>
                        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 text-center">
                            Hold the mic (15s max) · Whisper transcript · Auto-expires in 24h
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function ClipCard({ clip, crewId, isManager, onDeleted }) {
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);
    const heardMarked = useRef(false);

    const src = clip.audio_b64 ? `data:${clip.mime_type || "audio/webm"};base64,${clip.audio_b64}` : "";
    const timeStr = (() => {
        try { return new Date(clip.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
    })();

    const onPlay = async () => {
        if (!audioRef.current || !src) return;
        if (playing) { audioRef.current.pause(); setPlaying(false); return; }
        try {
            await audioRef.current.play();
            setPlaying(true);
            if (!heardMarked.current) {
                heardMarked.current = true;
                axios.post(`${API}/voice/heard`, { clip_id: clip.id, crew_id: crewId }).catch(() => {});
            }
        } catch { /* ignore */ }
    };

    const onDelete = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete this clip from ${clip.sender_name}?`)) return;
        try {
            await axios.post(`${API}/voice/clip-delete`, { actor_id: crewId, clip_id: clip.id });
            toast.success("Clip deleted.");
            onDeleted?.();
        } catch { toast.error("Couldn't delete."); }
    };

    const isEmergency = clip.priority === "emergency";
    const isRaiseHand = clip.priority === "raise_hand";

    return (
        <div className={`rounded-2xl border p-3 ${isEmergency ? "border-red-300 bg-red-50" : isRaiseHand ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid={`walkie-clip-${clip.id}`}>
            <div className="flex items-center gap-3">
                {src ? (
                    <button
                        onClick={onPlay}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors flex-shrink-0 ${playing ? "bg-rose-500" : "bg-orange-500 hover:bg-orange-600"}`}
                        data-testid={`walkie-play-${clip.id}`}
                        aria-label={playing ? "Pause" : "Play"}
                    >
                        <Play weight="fill" className="w-4 h-4" />
                    </button>
                ) : (
                    <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center text-white flex-shrink-0">
                        <Hand weight="fill" className="w-4 h-4" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-black text-slate-800 flex items-center gap-1.5">
                        {isEmergency && <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full bg-red-600 text-white">🚨 Emergency</span>}
                        {clip.sender_name} <span className="text-slate-400 font-normal">· {timeStr}{src ? ` · ${clip.duration_s?.toFixed?.(1) || "0"}s` : ""}</span>
                    </div>
                    {clip.transcript ? (
                        <div className="text-xs text-slate-700 leading-snug mt-0.5">{clip.transcript}</div>
                    ) : (
                        <div className="text-xs text-slate-400 italic mt-0.5">(transcript unavailable — tap to play)</div>
                    )}
                </div>
                {isManager && (
                    <button
                        onClick={onDelete}
                        className="text-[10px] font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-100 px-2 py-1 rounded-md flex-shrink-0"
                        data-testid={`walkie-delete-${clip.id}`}
                    >
                        Delete
                    </button>
                )}
            </div>
            {src && <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)} preload="none" />}
        </div>
    );
}
